import { useEffect, useState } from 'react'
import { useTheme } from './ThemeProvider'
import { getVersionInfo, formatVersion } from '../../utils/version'

interface DevBannerProps {
  apiUrl?: string
}

interface StalenessState {
  clientVersion: string
  apiVersion: string | null
  isStale: boolean
}

const IS_DEV = import.meta.env.DEV

export function DevBanner({ apiUrl }: DevBannerProps) {
  const { theme } = useTheme()
  const [staleness, setStaleness] = useState<StalenessState>({
    clientVersion: '',
    apiVersion: null,
    isStale: false,
  })

  useEffect(() => {
    async function checkVersion() {
      const info = await getVersionInfo()
      const clientVersion = formatVersion(info)

      let apiVersion: string | null = null
      let isStale = false

      if (apiUrl) {
        try {
          const response = await fetch(`${apiUrl}/health`)
          const health = await response.json() as { version?: string }
          if (health.version) {
            apiVersion = health.version
            isStale = health.version !== `${info.major}.${info.minor}.${info.patch}`
          }
        } catch {
          apiVersion = 'unreachable'
        }
      }

      setStaleness({ clientVersion, apiVersion, isStale })
    }

    void checkVersion()
  }, [apiUrl])

  if (!IS_DEV) return null

  const staleColour = staleness.isStale ? theme.colours.accent.warning : theme.colours.accent.success

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing.md,
        padding: `2px ${theme.spacing.md}`,
        backgroundColor: `${theme.colours.accent.warning}20`,
        borderBottom: `1px solid ${theme.colours.accent.warning}60`,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.xs,
        color: theme.colours.text.secondary,
        pointerEvents: 'none',
      }}
    >
      <span style={{ color: theme.colours.accent.warning, fontWeight: theme.typography.fontWeight.bold }}>
        DEV
      </span>

      {staleness.clientVersion && (
        <span>client {staleness.clientVersion}</span>
      )}

      {staleness.apiVersion && (
        staleness.apiVersion === 'unreachable' ? (
          <span style={{ color: theme.colours.accent.error }}>API unreachable</span>
        ) : (
          <span style={{ color: staleColour }}>
            {`API ${staleness.apiVersion}${staleness.isStale ? ' (stale)' : ''}`}
          </span>
        )
      )}
    </div>
  )
}
